import { add, sub } from "date-fns";
import { RuleOperator, defaultErrorMessage, type DurationValue } from "#lib/components/page/components/form/validation/schemaBuilder.js";
import { Logger } from "#lib/logging.js";
import type { AnySchemaObject, ErrorObject, SchemaValidateFunction, ValidateFunction } from "ajv";

// Treat invalid validation schemas as failing validation so the user sees an error instead of an invalid pattern being accepted.
function invalidSchemaValidator<T = unknown>(error: unknown): ValidateFunction<T> {
	Logger.error("Failed to compile form validation schema", { err: error });
	const validate = (() => {
		validate.errors = [{ keyword: "invalidSchema", instancePath: "", schemaPath: "", params: {}, message: defaultErrorMessage } satisfies ErrorObject];
		return false;
	}) as unknown as ValidateFunction<T>;
	return validate;
}

export async function getValidator(schema: string) {
	const { Ajv } = await import("ajv");
	const ajv = new Ajv();
	try {
		return ajv.compile(JSON.parse(schema));
	} catch (e) {
		return invalidSchemaValidator(e);
	}
}

export const durationKeyword = {
	keyword: "duration",
	validate: function validate(schemaValue: DurationValue, data: string, parentSchema?: AnySchemaObject) {
		if (!data || !schemaValue || !schemaValue.value || !schemaValue.unit) {
			return true;
		}

		const input = new Date(data);
		const today = new Date();
		const plus = add(today, { [schemaValue.unit]: schemaValue.value });
		const minus = sub(today, { [schemaValue.unit]: schemaValue.value });

		let valid: boolean;
		if (schemaValue.operator === RuleOperator.DATE_AT_LEAST) {
			// be at least duration before today
			valid = input <= minus;
		} else if (schemaValue.operator === RuleOperator.DATE_NO_MORE_THAN) {
			// be no more than duration before today
			valid = input <= minus || input > today;
		} else if (schemaValue.operator === RuleOperator.DATE_WITHIN) {
			// be within duration after today
			valid = input >= today && input <= plus;
		} else {
			// be more than duration after today
			valid = input > plus;
		}

		if (!valid) {
			(validate as SchemaValidateFunction).errors = [{ keyword: "duration", message: parentSchema?.errorMessage.pattern }];
		}
		return valid;
	},
	errors: true,
};

export async function getValidatorWithErrors(schema: string): Promise<ValidateFunction<unknown>> {
	const { Ajv } = await import("ajv");
	const { default: ajvErrors } = await import("ajv-errors");
	const { default: addFormats } = await import("ajv-formats");
	const ajv = new Ajv({
		strict: true,
		allErrors: true,
		useDefaults: true,
	});
	ajvErrors(ajv);
	addFormats(ajv);

	ajv.addKeyword(durationKeyword);
	try {
		return ajv.compile(JSON.parse(schema));
	} catch (e) {
		return invalidSchemaValidator(e);
	}
}
